import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { buildWhatsAppLink } from "../lib/contact";
import { SuryaLogoMark } from "./SuryaLogo";

const NAV_LINKS = [
    { id: "home", label: "Home" },
    { id: "services", label: "Services" },
    { id: "fleet", label: "Fleet" },
    { id: "clientele", label: "Clientele" },
    { id: "about", label: "About" },
    { id: "contact", label: "Contact" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("home");

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 12);
            // Active section detection
            const offsets = NAV_LINKS.map(({ id }) => {
                const el = document.getElementById(id);
                if (!el) return { id, top: Infinity };
                const rect = el.getBoundingClientRect();
                return { id, top: Math.abs(rect.top - 100) };
            });
            offsets.sort((a, b) => a.top - b.top);
            if (offsets[0]) setActive(offsets[0].id);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNavClick = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo */}
                    <button
                        type="button"
                        onClick={() => handleNavClick("home")}
                        data-testid="nav-logo"
                        className="flex items-center gap-3 group focus-ring"
                        aria-label="Surya Crane Service — Home"
                    >
                        <SuryaLogoMark size={48} className="rounded-md shadow-md shrink-0" />
                        <span className="hidden sm:flex flex-col items-start leading-none">
                            <span className="font-display text-lg md:text-xl text-white tracking-wide">
                                SURYA CRANE
                            </span>
                            <span className="font-body text-[10px] uppercase tracking-[0.25em] text-[#FFC107]">
                                Crane Services
                            </span>
                        </span>
                    </button>

                    {/* Desktop nav */}
                    <nav
                        className="hidden md:flex items-center gap-8"
                        aria-label="Primary"
                    >
                        {NAV_LINKS.map((link) => (
                            <button
                                key={link.id}
                                type="button"
                                onClick={() => handleNavClick(link.id)}
                                data-testid={`nav-link-${link.id}`}
                                className={`relative font-body text-sm uppercase tracking-[0.18em] transition-colors duration-200 focus-ring ${
                                    active === link.id
                                        ? "text-white"
                                        : "text-neutral-400 hover:text-white"
                                }`}
                            >
                                {link.label}
                                <span
                                    className={`absolute -bottom-2 left-0 h-[2px] bg-[#FFC107] transition-all duration-300 ${
                                        active === link.id
                                            ? "w-full"
                                            : "w-0 group-hover:w-full"
                                    }`}
                                />
                            </button>
                        ))}
                    </nav>

                    {/* Desktop CTA */}
                    <a
                        href={buildWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-cta"
                        className="hidden md:inline-flex items-center gap-2 bg-[#FFC107] hover:bg-[#E0A800] text-black font-body font-bold text-sm uppercase tracking-wider px-5 py-3 transition-all duration-200 hover:-translate-y-0.5 focus-ring"
                    >
                        <MessageCircle size={16} strokeWidth={2.5} />
                        WhatsApp Now
                    </a>

                    {/* Mobile toggle */}
                    <button
                        type="button"
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 text-white focus-ring"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X size={26} /> : <Menu size={26} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            <div
                className={`md:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 overflow-hidden transition-[max-height,opacity] duration-300 ${
                    open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
                }`}
                data-testid="nav-mobile-menu"
            >
                <div className="px-5 py-6 flex flex-col gap-1">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            type="button"
                            onClick={() => handleNavClick(link.id)}
                            data-testid={`nav-mobile-link-${link.id}`}
                            className="text-left font-body text-base uppercase tracking-[0.18em] text-neutral-200 hover:text-[#FFC107] py-3 border-b border-white/5 transition-colors"
                        >
                            {link.label}
                        </button>
                    ))}
                    <a
                        href={buildWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-mobile-whatsapp-cta"
                        className="mt-4 inline-flex items-center justify-center gap-2 bg-[#FFC107] text-black font-body font-bold text-sm uppercase tracking-wider px-5 py-4"
                    >
                        <MessageCircle size={18} strokeWidth={2.5} />
                        WhatsApp Now
                    </a>
                </div>
            </div>
        </header>
    );
};
